import React from "react"
import { Card, Button, Tag, Flex, Typography, Dropdown } from "antd"
import { MoreOutlined, LeftOutlined } from "@ant-design/icons"
import { spacings } from "themes/variables"
import IconWithBg from "ui/IconWithBg"
import { DSIcon } from "ui/Icon/DSIcon"
import PropTypes from "prop-types"
import PermissionGate from "components/PermissionGate"
import { MODULES, ACTIONS } from "utils/permissions"
import styles from "./index.module.css"

const { Title } = Typography

/**
 * HeaderSection component displays equipment header with image, info, and actions
 * @param {object} equipment - Mapped equipment data
 * @param {object} equipmentData - Raw equipment data from API
 * @param {object} screens - Ant Design breakpoint state
 * @param {Function} onEdit - Edit handler
 * @param {Function} onBack - Back navigation handler
 * @param {Function} onMenuClick - Menu click handler
 * @param {Array} menuItems - Menu items for dropdown
 */
const HeaderSection = ({ equipment, equipmentData, screens, onEdit, onBack, onMenuClick, menuItems }) => {
  return (
    <>
      {/* Navigation Header */}
      <Flex gap={16} className={styles.navHeader}>
        <IconWithBg icon={<LeftOutlined />} onClick={onBack} />
        <Title level={3}>Resource details</Title>
      </Flex>

      {/* Header Card */}
      <Card className={styles.headerCard}>
        <Flex justify="space-between" align="flex-start" wrap="wrap" gap={spacings.M}>
          <Flex gap={16} align="flex-start">
            {/* Equipment Image */}
            <div className={styles.equipmentImage}>
              {equipment?.media?.[0]?.preview_url || equipmentData?.header?.image ? (
                <img
                  src={equipment?.media?.[0]?.preview_url || equipmentData?.header?.image}
                  alt={equipment?.name}
                  style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }}
                />
              ) : (
                <div className={styles.imagePlaceholder}>
                  <DSIcon name="boat" width={50} height={50} />
                </div>
              )}
            </div>

            {/* Equipment Info */}
            <Flex vertical gap={8}>
              <Flex align="center" gap={12}>
                <Title level={3} className={styles.equipmentTitle}>
                  {equipment?.name || "Equipment Name"}
                </Title>
                <Tag color={equipment?.is_active ? "green" : "red"} className={styles.statusTag}>
                  {equipment?.is_active ? "Active" : "Deactivated"}
                </Tag>
              </Flex>
              <Tag className={styles.quantityTag}>QTY: {equipmentData?.header?.total_assets || 0}</Tag>
              <Tag className={styles.categoryTag}>{equipment?.category?.name || "Category"}</Tag>
            </Flex>
          </Flex>

          {/* Action Buttons */}
          <Flex gap={8} className={styles.headerActions}>
            <PermissionGate module={MODULES.EQUIPMENT} action={ACTIONS.EDIT}>
              <Button type="primary" onClick={onEdit}>
                Edit
              </Button>
            </PermissionGate>
            {menuItems.length > 0 && (
              <Dropdown menu={{ items: menuItems, onClick: onMenuClick }} trigger={["click"]}>
                <Button type="default" className={screens.md ? "" : styles.moreButton} icon={screens.md ? <MoreOutlined /> : null}>
                  {screens.md ? null : "More"}
                </Button>
              </Dropdown>
            )}
          </Flex>
        </Flex>
      </Card>
    </>
  )
}

HeaderSection.propTypes = {
  equipment: PropTypes.shape({
    name: PropTypes.string,
    is_active: PropTypes.bool,
    category: PropTypes.shape({
      name: PropTypes.string
    }),
    media: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
        preview_url: PropTypes.string
      })
    )
  }),
  equipmentData: PropTypes.shape({
    header: PropTypes.shape({
      image: PropTypes.string,
      total_assets: PropTypes.number
    })
  }),
  screens: PropTypes.shape({
    md: PropTypes.bool
  }),
  onEdit: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onMenuClick: PropTypes.func.isRequired,
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired
}

HeaderSection.defaultProps = {
  equipment: null,
  equipmentData: null,
  screens: {}
}

export default HeaderSection
